import { makeAutoObservable, observable, reaction } from 'mobx';
import type { ReactNode } from 'react';
import type { RailTab, PageAction } from '@/features/layout/types';
import { loadRailWidth, saveRailWidth, RAIL_WIDTH_MIN, RAIL_WIDTH_MAX } from '@/features/layout/logic';
import { entityStore } from './EntityStore';
import { DETACHED_TAB_ID, tabsStore } from './TabsStore';

const NO_ACTIONS: PageAction[] = [];

class RailStore {
  isOpen = false;
  activeTab: RailTab = 'resources';
  width: number;
  /** Quick actions per tab, registered by the page in that tab (usePageActions). */
  private actionsByTab = new Map<string, PageAction[]>();
  /**
   * The page's settings, per tab, ready to render (usePageSettings).
   *
   * A node rather than a description of one: the panel shows the *same*
   * settings as the navbar's menu, and the only way for one component to serve
   * both is for the page to hand it over already built. The page wraps it in
   * the contexts it needs, since the rail is a sibling of the panes and sits
   * under none of their providers.
   *
   * Shallow on purpose, and declared observable here rather than left to
   * makeAutoObservable: the values are React elements, which are frozen, and
   * deep observability would try to rewrite them in place.
   */
  private settingsByTab = observable.map<string, ReactNode>(undefined, { deep: false });

  constructor() {
    makeAutoObservable(this);
    this.width = loadRailWidth();

    // Actions of a tab that no longer exists would otherwise keep its Actions
    // tab alive for whatever tab reuses the id.
    reaction(
      () => tabsStore.liveTabIds.join('\n'),
      (ids) => this.prune(new Set(ids.split('\n'))),
    );
  }

  /** Quick actions of the tab in front. */
  get pageActions(): PageAction[] {
    return this.actionsByTab.get(tabsStore.activeTabId) ?? NO_ACTIONS;
  }

  /** Settings of the tab in front, or null when its page has none. */
  get pageSettings(): ReactNode {
    return this.settingsByTab.get(tabsStore.activeTabId) ?? null;
  }

  /**
   * Whether the tab in front gives the rail anything to show.
   *
   * Every tab draws from one of three sources: the settings its page
   * published, quick actions the page registered, or the entity the page put
   * into context. With none of them, the
   * panel only has "select an item" to say — but it stays reachable, and the
   * icon strip stays on screen with its tabs greyed out, so the panel does not
   * vanish from the interface the moment a page has nothing for it.
   */
  get hasContent(): boolean {
    return (
      this.pageActions.length > 0 ||
      this.pageSettings !== null ||
      entityStore.currentEntity !== null
    );
  }

  toggle() {
    this.isOpen = !this.isOpen;
  }

  open() {
    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
  }

  setTab(tab: RailTab) {
    this.activeTab = tab;
    this.isOpen = true;
  }

  resize(width: number) {
    this.width = Math.min(RAIL_WIDTH_MAX, Math.max(RAIL_WIDTH_MIN, width));
    saveRailWidth(this.width);
  }

  setPageActions(tabId: string, actions: PageAction[]) {
    this.actionsByTab.set(tabId, actions);
  }

  clearPageActions(tabId: string) {
    this.actionsByTab.delete(tabId);
  }

  setPageSettings(tabId: string, settings: ReactNode) {
    this.settingsByTab.set(tabId, settings);
  }

  clearPageSettings(tabId: string) {
    this.settingsByTab.delete(tabId);
  }

  private prune(live: Set<string>) {
    for (const key of Array.from(this.actionsByTab.keys())) {
      if (key !== DETACHED_TAB_ID && !live.has(key)) this.actionsByTab.delete(key);
    }
    for (const key of Array.from(this.settingsByTab.keys())) {
      if (key !== DETACHED_TAB_ID && !live.has(key)) this.settingsByTab.delete(key);
    }
  }
}

export const railStore = new RailStore();
